import { isAttritionQtyColumn, isRealBomLine } from "./quoteQuantities";

export const DIGIKEY_COLUMNS = [
  "Digikey Description",
  "Datasheet",
  "Package",
  "Lead Classification",
  "Mounting Type",
  "Part Status",
  "U.S. Import Tariff",
  "Country of Origin",
];

/** Last DigiKey column — used to host the optional refresh-details link. */
export const DIGIKEY_REFRESH_DETAILS_COLUMN = "Country of Origin";

export const DIGIKEY_REFRESH_DETAILS_LABEL = "Refresh missing DigiKey details";
export const DIGIKEY_REFRESH_DETAILS_LOADING_LABEL = "Refreshing DigiKey details…";

export const ALTERNATE_RECOMMENDATION_LABELS = {
  strongly_recommended: "Strongly recommended",
  recommended: "Recommended",
  default_lowest_unit_price: "Default (Lowest Unit Price)",
  // AI suggestion verdicts (Suggest Alternates)
  drop_in: "Best drop-in",
  usable_with_caveat: "Use with caveat",
  not_recommended: "Not recommended",
  obsolete_in_stock: "Obsolete but in stock",
  ai_suggested: "AI Suggested",
};

export const alternateRecommendationText = (label) => {
  const key = String(label || "").trim();
  return ALTERNATE_RECOMMENDATION_LABELS[key] || "";
};

/** Shorter recommendation label shown on BOM lines and Excel export. */
export const alternateRecommendationBomLineText = (label, text) => {
  const key = String(label || "").trim();
  if (key === "ai_suggested") {
    return ALTERNATE_RECOMMENDATION_LABELS.ai_suggested;
  }

  const explicit = String(text || "").trim();
  if (explicit) {
    if (
      explicit === ALTERNATE_RECOMMENDATION_LABELS.default_lowest_unit_price ||
      /^default\s*\(\s*lowest unit price\s*\)$/i.test(explicit)
    ) {
      return "Lowest Unit Price";
    }
    if (/^ai suggested$/i.test(explicit)) {
      return ALTERNATE_RECOMMENDATION_LABELS.ai_suggested;
    }
    return explicit;
  }

  if (key === "default_lowest_unit_price") {
    return "Lowest Unit Price";
  }
  return alternateRecommendationText(key);
};

export const hasAlternateRecommendationBadge = (label) => {
  const key = String(label || "").trim();
  return (
    key === "strongly_recommended" ||
    key === "recommended" ||
    key === "default_lowest_unit_price" ||
    key === "drop_in" ||
    key === "usable_with_caveat" ||
    key === "not_recommended" ||
    key === "obsolete_in_stock" ||
    key === "ai_suggested"
  );
};

export const alternateRecommendationBadgeColor = (label) => {
  // drop_in is green, usable_with_caveat is yellow — same idea as DigiKey recs
  if (label === "strongly_recommended" || label === "drop_in") {
    return "success";
  }
  if (label === "recommended") {
    return "primary";
  }
  if (label === "ai_suggested") {
    return "info";
  }
  if (label === "usable_with_caveat" || label === "obsolete_in_stock") {
    return "warning";
  }
  if (label === "default_lowest_unit_price" || label === "not_recommended") {
    return "secondary";
  }
  return "secondary";
};

export const isVisibleAlternateRecommendationLabel = (label) => {
  const key = String(label || "").trim();
  return (
    key === "strongly_recommended" ||
    key === "recommended" ||
    key === "drop_in"
  );
};

const lineSelectedAlternateIsAi = (line, substituteMatch) => {
  if (isOriginalObsoleteAlternate(substituteMatch)) {
    return false;
  }
  if (isAiAlternateRow(substituteMatch)) {
    return true;
  }
  if (String(line?.selected_alternate_group || "").toLowerCase() === "ai") {
    return true;
  }
  const type = String(
    line?.selected_alternate_type || substituteMatch?.substitute_type || "",
  ).toLowerCase();
  return type.indexOf("ai") !== -1;
};

/** Recommendation badge for the currently selected obsolete/unmatched alternate. */
export const resolveLineSelectedAlternateRecommendation = (line) => {
  if (!hasLineAlternateSelected(line)) {
    return { label: "", text: "" };
  }

  const selectedMpn = String(line.selected_alternate_mpn || "")
    .trim()
    .toUpperCase();
  if (!selectedMpn) {
    return { label: "", text: "" };
  }

  const substituteMatch = (Array.isArray(line.substitutes) ? line.substitutes : []).find(
    (item) =>
      String(
        item?.manufacturer_part_number || item?.manufacturerPartNumber || "",
      )
        .trim()
        .toUpperCase() === selectedMpn,
  );
  if (lineSelectedAlternateIsAi(line, substituteMatch)) {
    return {
      label: "ai_suggested",
      text: ALTERNATE_RECOMMENDATION_LABELS.ai_suggested,
    };
  }
  if (substituteMatch?.recommendation_label) {
    const label = String(substituteMatch.recommendation_label).trim();
    return {
      label,
      text:
        String(substituteMatch.recommendation_text || "").trim() ||
        alternateRecommendationText(label),
    };
  }

  const preselectMpn = String(line.preselect_alternate_mpn || "")
    .trim()
    .toUpperCase();
  if (
    line.recommendation_label &&
    (!preselectMpn || preselectMpn === selectedMpn)
  ) {
    const label = String(line.recommendation_label).trim();
    return {
      label,
      text:
        String(line.recommendation_text || "").trim() ||
        alternateRecommendationText(label),
    };
  }

  return { label: "", text: "" };
};

/** MPN DigiKey refresh/sync should fetch: the selected suggested part only. */
export const getDigiKeyRefreshPartNumber = (line) =>
  String(line?.selected_alternate_mpn || "").trim();

export const getDigiKeyRefreshOriginalPartNumber = (line) =>
  String(
    line?.unmatched_original_mpn ||
      line?.obsolete_part_number ||
      line?.manufacturer_part_number ||
      "",
  ).trim();

const isEmptyDigiKeyDisplayValue = (value) => {
  if (value == null) {
    return true;
  }
  if (typeof value === "string") {
    const text = value.trim().toLowerCase();
    return (
      text === "" ||
      text === "n/a" ||
      text === "unknown" ||
      text === "-" ||
      text === "–" ||
      text === "—"
    );
  }
  if (typeof value === "object") {
    if (Array.isArray(value.description)) {
      return !String(value.description[0]?.link || "").trim();
    }
    if (Array.isArray(value.part_status)) {
      return value.part_status.every((row) => {
        const status = String(row?.value || "")
          .trim()
          .toLowerCase();
        return status === "" || status === "n/a";
      });
    }
  }
  return false;
};

const isPlaceholderRohsStatus = (value) => {
  const text = String(value || "").trim();
  if (!text) {
    return true;
  }
  const normalized = text.toLowerCase();
  return normalized === "n/a" || normalized === "nexar_need_upgrade";
};

/** DigiKey RoHS from stored enrichment, quote distributor, or My Lists payload. */
export const resolveRohsStatus = (part) => {
  if (!part || typeof part !== "object") {
    return "";
  }
  const candidates = [
    part.ROHSStatus,
    part.rohs_status,
    part.rohsStatusMessage,
    part.RoHSStatus,
    part.rohsStatus,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const text = String(candidates[i] || "").trim();
    if (!isPlaceholderRohsStatus(text)) {
      return text;
    }
  }
  return "";
};

export const getRohsValueFromPartStatusCell = (partStatus) => {
  const rows = partStatus?.part_status;
  if (!Array.isArray(rows)) {
    return "";
  }
  const row = rows.find((entry) => /rohs/i.test(String(entry?.key || "")));
  const text = String(row?.value || "").trim();
  return isPlaceholderRohsStatus(text) ? "" : text;
};

/**
 * True when a selected alternate still lacks DigiKey detail columns
 * (typical after My Lists substitute-only save).
 */
export const lineNeedsDigiKeyDetailsRefresh = (line) => {
  if (!line || !hasLineAlternateSelected(line)) {
    return false;
  }

  return (
    isEmptyDigiKeyDisplayValue(line.Datasheet) ||
    isEmptyDigiKeyDisplayValue(line.Package) ||
    isEmptyDigiKeyDisplayValue(line["Part Status"]) ||
    isEmptyDigiKeyDisplayValue(line["Country of Origin"])
  );
};

/** Stable key for in-flight DigiKey detail refresh on a BOM line. */
export const getDigiKeyRefreshLineKey = (line) => {
  if (!line) {
    return "";
  }
  const mpn = getDigiKeyRefreshPartNumber(line);
  if (!mpn) {
    return "";
  }
  const lineKey =
    line._bomLineIndex != null
      ? `idx:${line._bomLineIndex}`
      : String(
          line.obsolete_part_number ||
            line.unmatched_original_mpn ||
            line.manufacturer_part_number ||
            ""
        ).trim();
  return `${lineKey}::${mpn}`;
};

/**
 * Merge DigiKey refresh payload into the selected alternate shape used by apply*.
 */
export const buildAlternateFromRefreshPart = (line, refreshPart) => {
  if (!refreshPart) {
    return null;
  }

  const mpn = String(
    refreshPart.manufacturer_part_number ||
      refreshPart.manufacturerPartNumber ||
      line?.selected_alternate_mpn ||
      ""
  ).trim();
  if (!mpn) {
    return null;
  }

  return {
    manufacturer_part_number: mpn,
    manufacturer:
      refreshPart.manufacturerName ||
      refreshPart.manufacturer ||
      line?.selected_alternate_manufacturer ||
      "",
    description: refreshPart.description || refreshPart.short_description || "",
    short_description:
      refreshPart.short_description || refreshPart.description || "",
    datasheetLink: refreshPart.datasheetLink || refreshPart.datasheet_url || "",
    datasheet_url: refreshPart.datasheet_url || refreshPart.datasheetLink || "",
    lifecycleStatus: refreshPart.lifecycleStatus || refreshPart.part_status || "",
    part_status: refreshPart.part_status || refreshPart.lifecycleStatus || "",
    ROHSStatus: resolveRohsStatus(refreshPart),
    package: refreshPart.package || "",
    package_classification: refreshPart.package_classification || "unknown",
    package_classification_label:
      refreshPart.package_classification_label || "Unknown",
    mounting_type: refreshPart.mounting_type || "",
    countryOfOrigin:
      refreshPart.countryOfOrigin || refreshPart.country_of_origin || "",
    country_of_origin:
      refreshPart.country_of_origin || refreshPart.countryOfOrigin || "",
    usImportTariff:
      refreshPart.usImportTariff || refreshPart.us_import_tariff || "",
    us_import_tariff:
      refreshPart.us_import_tariff || refreshPart.usImportTariff || "",
    digikey_part_number: refreshPart.digikey_part_number || "",
    part_detail_url:
      refreshPart.partDetailUrl || refreshPart.part_detail_url || "",
    substitute_type: line?.selected_alternate_type || "Suggested",
    substitute_group: line?.selected_alternate_group || "",
    source: "digikey_refresh",
  };
};

export const SMT_SECTION_TITLE = "Surface Mount Components (SMT)";
export const THT_SECTION_TITLE = "Through Hole Components (THT)";

/**
 * Flatten DigiKey category / mounting payloads (string, list, or nested object).
 */
const mountingSourceText = (value) => {
  if (value == null || value === false) {
    return "";
  }
  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }
  if (Array.isArray(value)) {
    return [...new Set(value.map(mountingSourceText).filter(Boolean))].join(" ");
  }
  if (typeof value === "object") {
    const preferred = ["valueText", "value", "name", "text", "label", "category"];
    const fromPreferred = preferred
      .map((key) => mountingSourceText(value[key]))
      .filter(Boolean);
    if (fromPreferred.length) {
      return [...new Set(fromPreferred)].join(" ");
    }
    return [
      ...new Set(Object.values(value).map(mountingSourceText).filter(Boolean)),
    ].join(" ");
  }
  return "";
};

const isBlankMountingValue = (value) => {
  const text = mountingSourceText(value).toLowerCase();
  return text === "" || text === "n/a" || text === "unknown";
};

const THROUGH_HOLE_KEYWORD = /through[\s-]?hole|thru[\s-]?hole/i;
const SURFACE_MOUNT_KEYWORD = /surface[\s-]?mount/i;

/**
 * Pull "Through Hole" or "Surface Mount" out of a DigiKey string.
 * Extra text before/after those keywords is ignored.
 */
export const extractMountingKeyword = (value) => {
  const text = mountingSourceText(value);
  if (isBlankMountingValue(text)) {
    return "";
  }
  if (THROUGH_HOLE_KEYWORD.test(text)) {
    return "Through Hole";
  }
  if (SURFACE_MOUNT_KEYWORD.test(text)) {
    return "Surface Mount";
  }
  return "";
};

/**
 * Prefer Mounting Type; if that field is missing, use Category.
 * When a keyword is present, store only Through Hole or Surface Mount.
 */
export const normalizeMountingType = (mountingType, category) => {
  const extracted = extractMountingKeyword(mountingType);
  if (extracted) {
    return extracted;
  }
  if (isBlankMountingValue(mountingType)) {
    return extractMountingKeyword(category);
  }
  return mountingSourceText(mountingType);
};

/**
 * Prefer product-attributes Category valueText ("Chip Resistor - Surface Mount")
 * over the top-level category ("Resistors").
 */
export const categoryTextForMounting = (part) => {
  if (!part || typeof part !== "object") {
    return "";
  }
  const attributes = part.product_attributes || part.productAttributes || [];
  let attributeCategory = "";
  if (Array.isArray(attributes)) {
    const row = attributes.find(
      (attr) => String(attr?.parameterText || "").trim().toLowerCase() === "category",
    );
    attributeCategory = String(row?.valueText || "").trim();
  }
  return [attributeCategory, part.category, part.Category]
    .map(mountingSourceText)
    .filter(Boolean)
    .join(" ");
};

/**
 * Prefer Supplier Device Package, then Package / Case, then stored package.
 * Ignores Packaging (tape/reel).
 */
const isUsablePackageValue = (value) => {
  const text = String(value || "").trim();
  if (!text) {
    return false;
  }
  const normalized = text.toLowerCase();
  return !["-", "–", "—", "n/a", "na", "none", "unknown", "."].includes(
    normalized,
  );
};

export const resolvePackageValue = (part) => {
  if (!part || typeof part !== "object") {
    return "";
  }
  const attributes = part.product_attributes || part.productAttributes || [];
  let supplier = "";
  let packageCase = "";
  const connectorValues = [];
  if (Array.isArray(attributes)) {
    attributes.forEach((attr) => {
      const label = String(attr?.parameterText || "")
        .trim()
        .toLowerCase();
      const value = String(attr?.valueText || "").trim();
      if (!label || !isUsablePackageValue(value) || label === "packaging") {
        return;
      }
      if (label === "supplier device package" && !supplier) {
        supplier = value;
        return;
      }
      if (
        !packageCase &&
        (label === "package / case" || label === "package/case")
      ) {
        packageCase = value;
        return;
      }
      if (label === "connector style" || label === "connector type") {
        connectorValues.push(value.toLowerCase());
      }
    });
  }
  if (isUsablePackageValue(supplier)) {
    return supplier;
  }
  if (isUsablePackageValue(packageCase)) {
    return packageCase;
  }
  const stored = String(part.package || "").trim();
  if (
    connectorValues.includes(stored.toLowerCase()) ||
    !isUsablePackageValue(stored)
  ) {
    return "";
  }
  return stored;
};

const SECTION_TITLES = [SMT_SECTION_TITLE, THT_SECTION_TITLE];

export const isBomSectionRow = (line) => {
  if (!line || typeof line !== "object") {
    return false;
  }
  if (line._section || line._section_title) {
    return true;
  }
  return Object.values(line).some((value) =>
    SECTION_TITLES.includes(String(value || "").trim()),
  );
};

/** True for the totals row appended after quoting (empty MPN, has Quote cells). */
export const isBomTotalRow = (line) => {
  if (
    !line ||
    typeof line !== "object" ||
    isBomSectionRow(line) ||
    isRealBomLine(line)
  ) {
    return false;
  }
  return Object.keys(line).some(
    (key) => /^Quote \d+$/.test(key) && line[key] !== "" && line[key] != null,
  );
};

/** Yellow Freight / Tariff Charge summary rows written by Excel export. */
export const isFreightTariffFooterRow = (line) => {
  if (!line || typeof line !== "object") {
    return false;
  }
  return Object.values(line).some((value) => {
    const text = String(value || "").trim();
    if (/^Freight:\s*/i.test(text) || /^Tariff Charge\s*:/i.test(text)) {
      return true;
    }
    if (/^Freight\s+[\d.]+\\*/i.test(text)) {
      return true;
    }
    if (/^Add\s+\d+(\.\d+)?%$/i.test(text)) {
      return true;
    }
    if (/^Total w\/ Tariff$/i.test(text)) {
      return true;
    }
    if (/^Assembly Specs( \((Lines|Qty)\))?$/i.test(text)) {
      return true;
    }
    if (
      /^(BOM Lines|SMT|TH|TH-\s*Solderpoints|LEAD LESS|BGA|SON|QFN)$/i.test(
        text,
      ) &&
      !isRealBomLine(line)
    ) {
      return true;
    }
    if (/^Total \(without tariff\)$/i.test(text) && !isRealBomLine(line)) {
      return true;
    }
    if (/^Total$/i.test(text) && !isRealBomLine(line)) {
      return true;
    }
    return false;
  });
};

/** True when a parsed Excel/BOM row has no meaningful cell values. */
export const isBlankBomLine = (line) => {
  if (!line || typeof line !== "object") {
    return true;
  }
  if (isFreightTariffFooterRow(line)) {
    return true;
  }
  if (isBomSectionRow(line) || isBomTotalRow(line)) {
    return false;
  }

  const ignoredKeys = new Set([
    "designator_color",
    "mounting_type",
    "_section",
    "_section_title",
    "_bomLineIndex",
    "part_id",
    "is_obsolete",
    "isObsolete",
    "is_unmatched",
    "isUnmatched",
    "obsolete_part_number",
    "unmatched_original_mpn",
    "selected_alternate_mpn",
    "selected_alternate_manufacturer",
    "selected_alternate_type",
    "selected_alternate_group",
    "selected_alternate_unit_price",
    "digikey_part_number",
    "quantity_available",
    "unit_price",
    "unit_price_display",
    "ai_suggestions",
    "ai_suggestions_at",
    "ai_suggestions_message",
    "ai_suggestions_rejected",
    "ai_suggestions_attempted",
    "alternate_selection_type",
    "obsolete_warning",
    "unmatched_warning",
    "digikey_errors",
    "substitutes",
    "substitutes_loaded",
    "alternate_count",
    "preselect_alternate_mpn",
    "recommendation_label",
    "recommendation_text",
    ...DIGIKEY_COLUMNS,
  ]);

  const hasMeaningfulValue = (value) => {
    if (value == null) {
      return false;
    }
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        return value.some(hasMeaningfulValue);
      }
      if (Array.isArray(value.part_status)) {
        return value.part_status.some((entry) =>
          hasMeaningfulValue(entry?.value)
        );
      }
      if (Array.isArray(value.description)) {
        return value.description.some(
          (entry) =>
            hasMeaningfulValue(entry?.value) ||
            hasMeaningfulValue(entry?.link) ||
            hasMeaningfulValue(entry?.linkText)
        );
      }
      return Object.values(value).some(hasMeaningfulValue);
    }

    const text = String(value).trim();
    if (!text) {
      return false;
    }
    return text.toLowerCase() !== "n/a";
  };

  return !Object.entries(line).some(([key, value]) => {
    if (ignoredKeys.has(key)) {
      return false;
    }
    return hasMeaningfulValue(value);
  });
};

/** Excel / CSS highlight for leadless package BOM lines (special assembly attention). */
export const LEADLESS_HIGHLIGHT_ARGB = "ffd9b3";
export const LEADLESS_ROW_CLASS = "bg-leadless";

/** Excel / CSS highlight for obsolete BOM lines with alternate selection. */
export const OBSOLETE_HIGHLIGHT_ARGB = "ffe6cc";
export const OBSOLETE_ROW_CLASS = "bg-obsolete";
export const OBSOLETE_ALT_SELECTED_ROW_CLASS = "obsolete-alt-selected";
export const OBSOLETE_RESOLVED_ROW_CLASS = "bg-obsolete-resolved";

/** Excel / CSS highlight for unmatched BOM lines (no DigiKey exact match). */
export const UNMATCHED_HIGHLIGHT_ARGB = "cce5ff";
export const UNMATCHED_ROW_CLASS = "bg-unmatched";
export const UNMATCHED_RESOLVED_ROW_CLASS = "bg-unmatched-resolved";

export const getObsoleteLineKey = (line) => {
  if (line?._bomLineIndex != null) {
    return `idx-${line._bomLineIndex}`;
  }
  return String(line?.manufacturer_part_number || "").trim().toUpperCase();
};

export const OBSOLETE_ALT_SELECTED_WARNING =
  "Obsolete Part - Alternate selected. Subject to customer approval.";
export const OBSOLETE_NO_ALT_WARNING =
  "Obsolete Part - No DigiKey alternate found.";
export const obsoleteAlternatesPrompt = (count) =>
  count > 0
    ? `Obsolete Part - ${count} alternate(s) may be available. Click to load and select.`
    : "Obsolete Part - Click to check DigiKey alternates.";

export const UNMATCHED_EXACT_MATCH_MESSAGE =
  "Exact match not found. Modify your entry or";
export const UNMATCHED_VIEW_OPTIONS_LABEL = "View Options";
export const UNMATCHED_VIEW_OPTIONS_LOADING_LABEL = "Loading suggestions…";
export const UNMATCHED_VIEW_OPTIONS_RETRY_LABEL = "Retry suggestions";
export const UNMATCHED_ALT_SELECTED_WARNING =
  "Unmatched part - replacement selected for quoting.";
export const UNMATCHED_DIGIKEY_WARNING =
  "No DigiKey match – select View Options to choose a suggested part.";

export const hasUnmatchedDigiKeyError = (value) => {
  const errors = Array.isArray(value)
    ? value
    : value?.digikey_errors || value?.errors || [];
  return errors.some((entry) => String(entry).trim() === "Unmatched");
};

/** DigiKey substitute/alternate listing sections (matches product detail page). */
export const SUBSTITUTE_LISTING_SECTIONS = [
  { key: "direct", listingCategory: "substitute", title: "Direct Substitutes" },
  { key: "parametric", listingCategory: "substitute", title: "Parametric Equivalent" },
  { key: "similar", listingCategory: "substitute", title: "Similar" },
  { key: "mfr_recommended", listingCategory: "substitute", title: "MFR Recommended" },
  { key: "alternate", listingCategory: "alternate", title: "Alternates" },
];

const normalizeSubstituteGroup = (group) => {
  const value = String(group || "").trim().toLowerCase();
  if (value === "alternate_packaging_mfr") {
    return "mfr_recommended";
  }
  return value;
};

const getListingCategory = (item) => {
  if (item?.listing_category) {
    return item.listing_category;
  }
  const group = normalizeSubstituteGroup(item?.substitute_group);
  return group === "alternate" ? "alternate" : "substitute";
};

/** Group flat substitute list into DigiKey-style sections for modal display. */
export const groupSubstitutesBySection = (items) =>
  SUBSTITUTE_LISTING_SECTIONS.map((section) => ({
    ...section,
    items: (items || []).filter((item) => {
      const category = getListingCategory(item);
      const group = normalizeSubstituteGroup(item?.substitute_group);
      if (section.listingCategory === "alternate") {
        return category === "alternate" || group === "alternate";
      }
      return category === "substitute" && group === section.key;
    }),
  })).filter((section) => section.items.length > 0);

/** True when DigiKey lead classification marks the line as leadless. */
export const isLeadlessLine = (line) => {
  if (
    !line ||
    typeof line !== "object" ||
    isBomSectionRow(line) ||
    isBomTotalRow(line)
  ) {
    return false;
  }
  return (
    String(line["Lead Classification"] || "")
      .trim()
      .toLowerCase() === "leadless"
  );
};

/** True when the BOM line is obsolete (DigiKey lifecycle). */
export const isObsoleteLine = (line) => {
  if (!line || typeof line !== "object" || isBomSectionRow(line) || isBomTotalRow(line)) {
    return false;
  }
  return line.is_obsolete === true || line.isObsolete === true;
};

/** True when DigiKey could not match the requested MPN (Unmatched error from My Lists). */
export const isUnmatchedLine = (line) => {
  if (!line || typeof line !== "object" || isBomSectionRow(line) || isBomTotalRow(line)) {
    return false;
  }
  if (isObsoleteLine(line)) {
    return false;
  }
  return (
    line.is_unmatched === true ||
    line.isUnmatched === true ||
    hasUnmatchedDigiKeyError(line)
  );
};

/** Shared key for obsolete/unmatched review modals. */
export const getLineReviewKey = getObsoleteLineKey;

/** True when user selected an alternate/suggested part for quoting. */
export const hasLineAlternateSelected = (line) =>
  line?.selected_alternate_mpn != null &&
  String(line.selected_alternate_mpn).trim() !== "";

/** Unique obsolete BOM lines for review modal. */
export const collectObsoleteBomLines = (bomlines) => {
  const seen = new Set();

  return (bomlines || [])
    .filter(isRealBomLine)
    .filter(isObsoleteLine)
    .filter((line) => {
      const key = getObsoleteLineKey(line);
      if (!key || seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
};

/** Unique unmatched BOM lines for review modal. */
export const collectUnmatchedBomLines = (bomlines) => {
  const seen = new Set();

  return (bomlines || [])
    .filter(isRealBomLine)
    .filter(isUnmatchedLine)
    .filter((line) => {
      const key = getLineReviewKey(line);
      if (!key || seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
};

export const getUnmatchedOriginalMpn = (line) =>
  String(
    line?.unmatched_original_mpn || line?.manufacturer_part_number || "",
  ).trim();

export const getSuggestedPartStockQty = (item) => {
  const raw =
    item?.quantity_available ??
    item?.quantityAvailable ??
    item?.formattedQuantityAvailable ??
    0;
  const qty = Number(String(raw).replace(/,/g, "").trim());
  return Number.isFinite(qty) ? qty : 0;
};

export const suggestedPartHasStock = (item) => getSuggestedPartStockQty(item) > 0;

export const ORIGINAL_OBSOLETE_GROUP = "original";

export const isOriginalObsoleteAlternate = (item) => {
  if (!item || typeof item !== "object") {
    return false;
  }
  if (item.is_original_obsolete === true) {
    return true;
  }
  const group = String(item.substitute_group || item.source || "").toLowerCase();
  return group === ORIGINAL_OBSOLETE_GROUP;
};

const alternateRowMpnKey = (item) =>
  String(item?.manufacturer_part_number || item?.manufacturerPartNumber || "")
    .trim()
    .toUpperCase();

/** In-stock obsolete original, shown first in the review modal. */
export const buildOriginalObsoleteAlternate = (line, enrichmentPart = {}) => {
  const src =
    enrichmentPart && typeof enrichmentPart === "object" ? enrichmentPart : {};
  const mpn = String(
    line?.obsolete_part_number ||
      line?.manufacturer_part_number ||
      src.manufacturerPartNumber ||
      src.manufacturer_part_number ||
      "",
  ).trim();
  if (!mpn) {
    return null;
  }
  const qty =
    getSuggestedPartStockQty(src) || getSuggestedPartStockQty(line);
  if (qty <= 0) {
    return null;
  }

  const unitPrice =
    src.unit_price != null && src.unit_price !== ""
      ? src.unit_price
      : line?.unit_price;

  return {
    manufacturer_part_number: mpn,
    manufacturer:
      src.manufacturerName ||
      src.manufacturer ||
      line?.manufacturer ||
      "",
    description: src.description || src.short_description || "",
    short_description: src.short_description || src.description || "",
    package: src.package || line?.Package || line?.package || "",
    digikey_part_number: src.digikey_part_number || line?.digikey_part_number || "",
    unit_price: unitPrice,
    unit_price_display: src.unit_price_display || line?.unit_price_display || "",
    quantity_available: qty,
    part_detail_url: src.partDetailUrl || src.part_detail_url || "",
    datasheetLink: src.datasheetLink || src.datasheet_url || "",
    lifecycleStatus: src.lifecycleStatus || src.part_status || "",
    substitute_type: "Original",
    substitute_group: ORIGINAL_OBSOLETE_GROUP,
    source: ORIGINAL_OBSOLETE_GROUP,
    listing_category: "original",
    recommendation_label: "obsolete_in_stock",
    recommendation_text: "Obsolete but in stock",
    notes: "Original obsolete part still has DigiKey stock.",
    is_original_obsolete: true,
  };
};

export const prependOriginalObsoleteAlternate = (
  substitutes,
  line,
  enrichmentPart,
) => {
  const list = Array.isArray(substitutes) ? [...substitutes] : [];
  const original = buildOriginalObsoleteAlternate(line, enrichmentPart || {});
  if (!original) {
    return list;
  }
  const key = original.manufacturer_part_number.toUpperCase();
  const rest = list.filter((item) => {
    if (isOriginalObsoleteAlternate(item)) {
      return false;
    }
    return alternateRowMpnKey(item) !== key;
  });
  return [original, ...rest];
};

/** DigiKey rows first, then AI cache rows for the same obsolete line. */
export const mergeAiSuggestionsIntoSubstitutes = (substitutes, aiSuggestions) => {
  const merged = Array.isArray(substitutes) ? [...substitutes] : [];
  const seen = {};
  merged.forEach((item, index) => {
    const key = alternateRowMpnKey(item);
    if (key) {
      seen[key] = index;
    }
  });
  (Array.isArray(aiSuggestions) ? aiSuggestions : []).forEach((item) => {
    const key = alternateRowMpnKey(item);
    if (!key) {
      return;
    }
    if (Object.prototype.hasOwnProperty.call(seen, key)) {
      return;
    }
    seen[key] = merged.length;
    merged.push(item);
  });
  return merged;
};

export const getUnmatchedViewOptionsCache = (line) =>
  (Array.isArray(line?.substitutes) ? line.substitutes : []).filter((item) => {
    const mpn = String(
      item?.manufacturer_part_number || item?.manufacturerPartNumber || "",
    ).trim();
    return mpn !== "" && suggestedPartHasStock(item);
  });

export const lineHasCachedViewOptions = (line) => {
  if (!line) {
    return false;
  }
  if (line.substitutes_loaded === true) {
    return true;
  }
  return getUnmatchedViewOptionsCache(line).length > 0;
};

/** Unique suggested parts selected for unmatched lines that still lack Country of Origin. */
export const lineHasMissingInfoSynced = (line) => {
  if (
    !line ||
    (line._missing_info_synced !== true && line.missing_info_synced !== true)
  ) {
    return false;
  }
  const selected = String(line.selected_alternate_mpn || "")
    .trim()
    .toUpperCase();
  const synced = String(
    line._missing_info_synced_mpn || line.missing_info_synced_mpn || "",
  )
    .trim()
    .toUpperCase();
  return !!selected && selected === synced;
};

export const markLineMissingInfoSynced = (row, mpn) => {
  if (!row) {
    return row;
  }
  const syncedMpn = String(mpn || row.selected_alternate_mpn || "").trim();
  if (!syncedMpn) {
    return row;
  }
  const next = {
    ...row,
    _missing_info_synced: true,
    _missing_info_synced_mpn: syncedMpn,
  };
  delete next.missing_info_synced;
  delete next.missing_info_synced_mpn;
  return next;
};

export const collectSelectedUnmatchedNeedingSync = (bomlines) => {
  const seen = new Set();

  return (bomlines || []).filter((line) => {
    if (
      !isRealBomLine(line) ||
      !isUnmatchedLine(line) ||
      !hasLineAlternateSelected(line) ||
      lineHasMissingInfoSynced(line) ||
      !isEmptyDigiKeyDisplayValue(line["Country of Origin"])
    ) {
      return false;
    }

    const mpn = String(line.selected_alternate_mpn || "")
      .trim()
      .toUpperCase();
    if (!mpn || seen.has(mpn)) {
      return false;
    }
    seen.add(mpn);
    return true;
  });
};

export const getSyncMissingInfoCount = (bomlines) =>
  collectSelectedUnmatchedNeedingSync(bomlines).length;

/**
 * Keep newly selected unmatched suggestions from the live BOM while a sync
 * is in flight, without discarding Country of Origin already written locally.
 */
export const mergeLatestUnmatchedSelections = (localRows, propRows) => {
  if (!Array.isArray(propRows) || propRows.length === 0) {
    return localRows || [];
  }
  if (!Array.isArray(localRows) || localRows.length === 0) {
    return propRows;
  }

  const localByIndex = new Map();
  localRows.forEach((row, index) => {
    if (isBomSectionRow(row)) {
      return;
    }
    const key =
      row?._bomLineIndex != null ? `idx:${row._bomLineIndex}` : `i:${index}`;
    localByIndex.set(key, row);
  });

  return propRows.map((propRow, index) => {
    if (isBomSectionRow(propRow)) {
      return propRow;
    }
    const key =
      propRow?._bomLineIndex != null
        ? `idx:${propRow._bomLineIndex}`
        : `i:${index}`;
    const localRow = localByIndex.get(key);
    if (!localRow) {
      return propRow;
    }

    const propMpn = String(propRow.selected_alternate_mpn || "")
      .trim()
      .toUpperCase();
    const localMpn = String(localRow.selected_alternate_mpn || "")
      .trim()
      .toUpperCase();

    if (localMpn && !propMpn) {
      return localRow;
    }
    if (propMpn && propMpn !== localMpn) {
      return propRow;
    }

    return localRow;
  });
};

export const lineMatchesSelectedAlternateMpn = (row, mpn) =>
  String(row?.selected_alternate_mpn || "")
    .trim()
    .toLowerCase() === String(mpn || "").trim().toLowerCase();

export const matchesBomLine = (row, line) =>
  row === line ||
  (row?._bomLineIndex != null &&
    line?._bomLineIndex != null &&
    row._bomLineIndex === line._bomLineIndex);

export const hasObsoleteAlternateSelected = hasLineAlternateSelected;

/** Alternate count available on an obsolete BOM line. */
export const getObsoleteAlternateCount = (line) => {
  if (!line) {
    return 0;
  }
  if (Array.isArray(line.substitutes) && line.substitutes.length > 0) {
    return line.substitutes.length;
  }
  if (line.alternate_count != null && line.alternate_count !== "") {
    return Number(line.alternate_count) || 0;
  }
  return 0;
};

export const getUnmatchedSuggestionCount = (line) => {
  if (!line) {
    return 0;
  }
  if (line.substitutes_loaded === true || Array.isArray(line.substitutes)) {
    return getUnmatchedViewOptionsCache(line).length;
  }
  if (line.alternate_count != null && line.alternate_count !== "") {
    return Number(line.alternate_count) || 0;
  }
  return 0;
};

/** True when BOM line already has substitute rows from digikey_enrichment. */
export const hasCachedSubstitutesOnLine = (line) =>
  Array.isArray(line?.substitutes) && line.substitutes.length > 0;

/** Returns an error message when alternate save failed, or null on success. */
export const getDigiKeyAlternateSaveFailureMessage = (
  saveResponse,
  fallback = "Failed to save selection."
) => {
  if (
    !saveResponse ||
    saveResponse.code == null ||
    !(saveResponse.code >= 200 && saveResponse.code < 400)
  ) {
    return (saveResponse && saveResponse.message) || fallback;
  }

  const results = saveResponse.data?.results;
  if (!Array.isArray(results) || !results.length) {
    return null;
  }

  const failed = results.find((item) => !item.saved);
  if (failed) {
    return failed.message || fallback;
  }

  return null;
};

/** Prefer fully enriched alternate returned by save API over view-option preview. */
export const getSavedAlternateFromResponse = (
  saveResponse,
  fallbackAlternate,
  match = {}
) => {
  const results = saveResponse?.data?.results;
  if (!Array.isArray(results)) {
    return fallbackAlternate;
  }

  const obsoleteMpn = String(match.obsolete_part_number || "").trim();
  const partId = match.part_id != null ? Number(match.part_id) : null;

  const saved = results.find((item) => {
    if (!item.saved || !item.alternate) {
      return false;
    }
    if (partId && Number(item.obsolete_part_id) === partId) {
      return true;
    }
    if (
      obsoleteMpn &&
      String(item.obsolete_part_number || "").trim() === obsoleteMpn
    ) {
      return true;
    }
    return false;
  });

  if (saved?.alternate) {
    return stripNestedSubstitutes(saved.alternate);
  }

  if (results.length === 1 && results[0].saved && results[0].alternate) {
    return stripNestedSubstitutes(results[0].alternate);
  }

  return fallbackAlternate;
};

const stripNestedSubstitutes = (alternate) => {
  if (!alternate || typeof alternate !== "object") {
    return alternate;
  }
  const {
    substitutes,
    substitutesLoaded,
    recommendedAlternate,
    alternateCount,
    obsoleteWarning,
    unmatchedWarning,
    ...row
  } = alternate;
  return row;
};

/** Row tooltip for obsolete lines (keeps Part Status cell compact). */
export const getObsoleteRowTooltip = (line) => {
  if (!isObsoleteLine(line)) {
    return "";
  }
  if (hasLineAlternateSelected(line)) {
    return OBSOLETE_ALT_SELECTED_WARNING;
  }
  return "Click row to select.";
};

export const getUnmatchedRowTooltip = (line) => {
  if (!isUnmatchedLine(line)) {
    return "";
  }
  return line.unmatched_warning || UNMATCHED_DIGIKEY_WARNING;
};

/** True when line uses a selected alternate for distributor quoting. */
export const lineUsesAlternateForQuote = (line) =>
  (isObsoleteLine(line) || isUnmatchedLine(line)) &&
  hasLineAlternateSelected(line);

/** MPN used for distributor quoting (alternate when selected). */
export const getQuoteMpn = (line) => {
  if (!line) return "";
  if (line.selected_alternate_mpn != null && String(line.selected_alternate_mpn).trim() !== "") {
    return String(line.selected_alternate_mpn).trim();
  }
  return String(line.manufacturer_part_number || "").trim();
};

/** Manufacturer shown in exports when an alternate/suggested part is selected. */
export const getExportManufacturer = (line) => {
  if (
    hasLineAlternateSelected(line) &&
    line.selected_alternate_manufacturer != null &&
    String(line.selected_alternate_manufacturer).trim() !== ""
  ) {
    return String(line.selected_alternate_manufacturer).trim();
  }
  return line?.manufacturer != null ? String(line.manufacturer) : "";
};

/** MPN shown in exports — selected alternate/suggestion when chosen, else BOM MPN. */
export const getExportManufacturerPartNumber = (line) => {
  if (hasLineAlternateSelected(line)) {
    return String(line.selected_alternate_mpn).trim();
  }
  return String(line?.manufacturer_part_number || "").trim();
};

/** Rich-text MPN cell for Excel (obsolete alternate or unmatched suggestion display). */
export const getExportManufacturerPartNumberCellValue = (line) => {
  if (!hasLineAlternateSelected(line)) {
    return null;
  }

  const selectedMpn = String(line.selected_alternate_mpn).trim();
  const originalMpn = String(
    (isUnmatchedLine(line)
      ? line.unmatched_original_mpn
      : line.obsolete_part_number) || line.manufacturer_part_number || ""
  ).trim();
  const originalLabel = isUnmatchedLine(line) ? "Unmatched" : "Obsolete";
  const { label, text } = resolveLineSelectedAlternateRecommendation(line);
  const richText = [
    {
      text: `${selectedMpn}\r\n`,
      font: { bold: true },
    },
  ];

  if (hasAlternateRecommendationBadge(label) && text) {
    const displayText = alternateRecommendationBomLineText(label, text);
    richText.push({
      text: `${displayText}\r\n`,
      font: {
        bold: true,
        color: {
          argb:
            label === "strongly_recommended"
              ? "1e7e34"
              : label === "recommended"
                ? "0c63e4"
                : label === "ai_suggested"
                  ? "6F42C1"
                : "6c757d",
        },
      },
    });
  }

  richText.push({
    text: `${originalLabel}: ${originalMpn}`,
    font: { color: { argb: "636e95" } },
  });

  return { richText };
};

const substituteToPartFields = (alternate) => {
  if (!alternate) return null;
  return {
    short_description: alternate.short_description || alternate.description || "",
    datasheetLink: alternate.datasheetLink || alternate.datasheet_url || "",
    lifecycleStatus: alternate.lifecycleStatus || alternate.part_status || "",
    ROHSStatus: resolveRohsStatus(alternate),
    package: alternate.package || "",
    package_classification: alternate.package_classification || "unknown",
    package_classification_label: alternate.package_classification_label || "Unknown",
    mounting_type: alternate.mounting_type || "",
    countryOfOrigin: alternate.countryOfOrigin || alternate.country_of_origin || "",
    usImportTariff: alternate.usImportTariff || alternate.us_import_tariff || "",
    manufacturerName: alternate.manufacturer || alternate.manufacturerName || "",
    digikey_part_number: alternate.digikey_part_number || "",
  };
};

/**
 * Keep SMT/THT mounting classification when the selected alternate lacks mounting_type.
 * Empty DigiKey fields must not wipe the line's mounting used by organizeBomByMountingType.
 */
const preserveMountingClassification = (previousLine, nextLine, partFields) => {
  if (!previousLine || !nextLine) {
    return nextLine;
  }

  const altMounting = String(
    normalizeMountingType(
      partFields?.mounting_type,
      categoryTextForMounting(partFields),
    ) || "",
  ).trim();
  if (!altMounting) {
    const preserved = normalizeMountingType(
      previousLine.mounting_type || previousLine["Mounting Type"],
      categoryTextForMounting(previousLine),
    );
    const prevMounting = String(previousLine.mounting_type || "").trim();
    const prevMountingLabel = String(previousLine["Mounting Type"] || "").trim();
    if (preserved) {
      nextLine.mounting_type = preserved;
      if (prevMountingLabel && prevMountingLabel.toLowerCase() !== "n/a") {
        nextLine["Mounting Type"] = preserved;
      }
    } else {
      if (prevMounting) {
        nextLine.mounting_type = previousLine.mounting_type;
      }
      if (prevMountingLabel && prevMountingLabel.toLowerCase() !== "n/a") {
        nextLine["Mounting Type"] = previousLine["Mounting Type"];
      }
    }
  }

  return nextLine;
};

/** Build alternate payload from merged enrichment API fields. */
export const buildAlternateFromEnrichmentPart = (enrichmentPart, selectedMpn) => {
  const mpn = String(selectedMpn || enrichmentPart?.selected_alternate_mpn || "").trim();
  if (!mpn || !enrichmentPart) {
    return null;
  }

  return {
    manufacturer_part_number: mpn,
    manufacturer:
      enrichmentPart.selected_alternate_manufacturer ||
      enrichmentPart.manufacturerName ||
      enrichmentPart.manufacturer ||
      "",
    description: enrichmentPart.description || enrichmentPart.short_description || "",
    short_description: enrichmentPart.short_description || enrichmentPart.description || "",
    datasheetLink: enrichmentPart.datasheetLink || enrichmentPart.datasheet_url || "",
    lifecycleStatus: enrichmentPart.lifecycleStatus || enrichmentPart.part_status || "",
    ROHSStatus: resolveRohsStatus(enrichmentPart),
    package: enrichmentPart.package || "",
    package_classification: enrichmentPart.package_classification || "unknown",
    package_classification_label:
      enrichmentPart.package_classification_label || "Unknown",
    mounting_type: enrichmentPart.mounting_type || "",
    countryOfOrigin: enrichmentPart.countryOfOrigin || enrichmentPart.country_of_origin || "",
    usImportTariff: enrichmentPart.usImportTariff || enrichmentPart.us_import_tariff || "",
    digikey_part_number: enrichmentPart.digikey_part_number || "",
    substitute_type: "Suggested",
    substitute_group: "view_options",
    source: "digikey",
  };
};

/** Preserve alternate/unmatched line metadata when rebuilding rows after auto-quote. */
export const preserveAlternateLineState = (prev, next) => {
  if (!prev || !next || !isRealBomLine(next)) {
    return next;
  }

  const shouldPreserve =
    hasLineAlternateSelected(prev) ||
    isObsoleteLine(prev) ||
    isUnmatchedLine(prev);
  if (!shouldPreserve) {
    return next;
  }

  const merged = { ...next };
  const keys = [
    "selected_alternate_mpn",
    "selected_alternate_manufacturer",
    "selected_alternate_type",
    "selected_alternate_group",
    "selected_alternate_unit_price",
    "digikey_part_number",
    "quantity_available",
    "unit_price",
    "unit_price_display",
    "ai_suggestions",
    "ai_suggestions_at",
    "ai_suggestions_message",
    "ai_suggestions_rejected",
    "ai_suggestions_attempted",
    "alternate_selection_type",
    "substitutes",
    "substitutes_loaded",
    "alternate_count",
    "preselect_alternate_mpn",
    "recommendation_label",
    "recommendation_text",
    "obsolete_part_number",
    "unmatched_original_mpn",
    "is_obsolete",
    "isObsolete",
    "is_unmatched",
    "isUnmatched",
    "obsolete_warning",
    "unmatched_warning",
    "digikey_errors",
    "part_id",
    "mounting_type",
    "_missing_info_synced",
    "_missing_info_synced_mpn",
    ...DIGIKEY_COLUMNS,
  ];

  keys.forEach((key) => {
    if (prev[key] !== undefined) {
      merged[key] = prev[key];
    }
  });

  if (hasLineAlternateSelected(prev)) {
    applyAlternateManufacturerToLine(
      prev,
      merged,
      prev.selected_alternate_manufacturer
    );
  }

  return merged;
};

/** Set Manufacturer column keys from the selected alternate (table reads these, not selected_alternate_manufacturer). */
const applyAlternateManufacturerToLine = (line, next, alternateManufacturer) => {
  const manufacturer = String(alternateManufacturer || "").trim();
  if (!manufacturer || !next) {
    return next;
  }

  const mfgKeys = findLineKeysByNormalizedHeader(line || next, "manufacturer");
  if (mfgKeys.length) {
    mfgKeys.forEach((key) => {
      next[key] = manufacturer;
    });
  } else {
    next.manufacturer = manufacturer;
  }

  return next;
};

/** True when a DigiKey table cell is empty / placeholder. */
const isBlankDigiKeyDisplayValue = (value) => {
  if (value == null || value === "") {
    return true;
  }
  if (typeof value === "string") {
    const text = value.trim().toLowerCase();
    return text === "" || text === "n/a" || text === "unknown";
  }
  if (typeof value === "object") {
    if (Array.isArray(value.part_status)) {
      return value.part_status.every((entry) =>
        isBlankDigiKeyDisplayValue(entry?.value)
      );
    }
    if (Array.isArray(value.description)) {
      return value.description.every(
        (entry) => !entry?.link && isBlankDigiKeyDisplayValue(entry?.value)
      );
    }
  }
  return false;
};

/** Keep already-enriched DigiKey columns when the alternate payload is sparse. */
const preserveEnrichedDigiKeyColumns = (previousLine, nextLine) => {
  if (!previousLine || !nextLine) {
    return nextLine;
  }
  DIGIKEY_COLUMNS.forEach((col) => {
    if (
      isBlankDigiKeyDisplayValue(nextLine[col]) &&
      !isBlankDigiKeyDisplayValue(previousLine[col])
    ) {
      nextLine[col] = previousLine[col];
    }
  });
  return nextLine;
};

/** Apply selected alternate DigiKey display + quote fields; keep original MPN visible. */
export const applySelectedAlternateToLine = (line, alternate) => {
  if (!line || !alternate) return line;

  const partFields = substituteToPartFields(alternate);
  const digiKeyFields = partFields ? buildDigiKeyFields(partFields) : {};
  const mpn = String(
    alternate.manufacturer_part_number || alternate.manufacturerPartNumber || ""
  ).trim();
  const alternateManufacturer = String(
    alternate.manufacturer || alternate.manufacturerName || ""
  ).trim();
  const obsoleteMpn = String(
    line.obsolete_part_number || line.manufacturer_part_number || ""
  ).trim();

  const next = {
    ...line,
    ...digiKeyFields,
    obsolete_part_number: obsoleteMpn,
    selected_alternate_mpn: mpn,
    selected_alternate_manufacturer: alternateManufacturer,
    selected_alternate_type:
      alternate.substitute_type ||
      (isAiAlternateRow(alternate) ? "AI Suggested" : ""),
    selected_alternate_group:
      alternate.substitute_group || (isAiAlternateRow(alternate) ? "ai" : ""),
    selected_alternate_unit_price:
      alternate.unit_price != null ? alternate.unit_price : line.selected_alternate_unit_price,
    digikey_part_number:
      alternate.digikey_part_number || line.digikey_part_number || "",
    obsolete_warning: OBSOLETE_ALT_SELECTED_WARNING,
    is_obsolete: true,
    isObsolete: true,
    substitutes: mergeSelectedIntoSubstitutes(line.substitutes, alternate),
    substitutes_loaded: true,
    recommendation_label: alternate.recommendation_label || line.recommendation_label || "",
    recommendation_text:
      alternate.recommendation_text ||
      line.recommendation_text ||
      alternateRecommendationText(alternate.recommendation_label || line.recommendation_label),
  };

  applyAlternateManufacturerToLine(line, next, alternateManufacturer);

  return preserveEnrichedDigiKeyColumns(
    line,
    preserveMountingClassification(line, next, partFields)
  );
};

/** Find the selected MPN in line.substitutes / alternateParts enrichment arrays. */
export const resolveAlternateFromLineEnrichment = (line, alternate) => {
  if (!alternate) {
    return null;
  }
  const mpn = String(
    alternate.manufacturer_part_number || alternate.manufacturerPartNumber || ""
  )
    .trim()
    .toUpperCase();
  if (!mpn) {
    return alternate;
  }

  const lists = [];
  if (Array.isArray(line?.substitutes)) {
    lists.push(...line.substitutes);
  }
  if (Array.isArray(line?.alternateParts)) {
    lists.push(...line.alternateParts);
  }

  const matched = lists.find((item) => {
    const itemMpn = String(
      item?.manufacturer_part_number || item?.manufacturerPartNumber || ""
    )
      .trim()
      .toUpperCase();
    return itemMpn === mpn;
  });

  return preferAlternateWithEnrichment(matched || alternate, alternate);
};

/** Prefer DigiKey display fields already present on the selected substitute. */
export const preferAlternateWithEnrichment = (selectedAlternate, savedAlternate) => {
  if (!selectedAlternate) {
    return savedAlternate || null;
  }
  if (!savedAlternate) {
    return selectedAlternate;
  }

  const merged = { ...savedAlternate, ...selectedAlternate };
  const keys = [
    "short_description",
    "description",
    "datasheetLink",
    "datasheet_url",
    "lifecycleStatus",
    "part_status",
    "ROHSStatus",
    "package",
    "package_classification",
    "package_classification_label",
    "mounting_type",
    "countryOfOrigin",
    "country_of_origin",
    "usImportTariff",
    "us_import_tariff",
    "digikey_part_number",
    "manufacturer",
    "manufacturerName",
    "recommendation_label",
    "recommendation_text",
    "unit_price",
    "unit_price_display",
    "quantity_available",
    "part_detail_url",
    "partDetailUrl",
  ];

  keys.forEach((key) => {
    const selectedValue = selectedAlternate[key];
    const savedValue = savedAlternate[key];
    if (
      (merged[key] == null || String(merged[key]).trim() === "") &&
      savedValue != null &&
      String(savedValue).trim() !== ""
    ) {
      merged[key] = savedValue;
    }
    if (
      selectedValue != null &&
      String(selectedValue).trim() !== ""
    ) {
      merged[key] = selectedValue;
    }
  });

  return merged;
};

const alternateRowMpn = (item) =>
  String(item?.manufacturer_part_number || item?.manufacturerPartNumber || "")
    .trim()
    .toUpperCase();

export const isAiAlternateRow = (item) => {
  const group = String(item?.substitute_group || item?.source || "").toLowerCase();
  if (group === "ai") {
    return true;
  }
  return String(item?.substitute_type || "").toLowerCase().indexOf("ai") !== -1;
};

/** Keep the full alternate list and patch DigiKey fields onto the selected row. */
export const mergeSelectedIntoSubstitutes = (existing, selected) => {
  if (!selected) {
    return Array.isArray(existing) ? existing : [];
  }

  const selectedMpn = alternateRowMpn(selected);
  const list = Array.isArray(existing) && existing.length ? existing : [];
  let found = false;
  const next = list.map((item) => {
    if (!selectedMpn || alternateRowMpn(item) !== selectedMpn) {
      return item;
    }
    found = true;
    const merged = preferAlternateWithEnrichment(selected, item);
    if (!isAiAlternateRow(item) && !isAiAlternateRow(selected)) {
      return merged;
    }
    return {
      ...merged,
      source: item.source || selected.source || "ai",
      substitute_group: item.substitute_group || selected.substitute_group || "ai",
      substitute_type:
        item.substitute_type || selected.substitute_type || "AI Suggested",
      recommendation_label:
        item.recommendation_label || merged.recommendation_label,
      recommendation_text:
        item.recommendation_text || merged.recommendation_text,
      notes: item.notes || item.recommendation_text || merged.notes,
    };
  });

  if (!found) {
    next.unshift(selected);
  }

  return next;
};

/** Apply user-selected DigiKey suggestion for an unmatched BOM line. */
export const applySelectedUnmatchedPartToLine = (
  line,
  alternate,
  allOptions = null
) => {
  if (!line || !alternate) return line;

  const partFields = substituteToPartFields(alternate);
  const digiKeyFields = partFields ? buildDigiKeyFields(partFields) : {};
  const mpn = String(
    alternate.manufacturer_part_number || alternate.manufacturerPartNumber || ""
  ).trim();
  const alternateManufacturer = String(
    alternate.manufacturer || alternate.manufacturerName || ""
  ).trim();
  const originalMpn = String(
    line.unmatched_original_mpn || line.manufacturer_part_number || ""
  ).trim();

  const optionList = Array.isArray(allOptions)
    ? allOptions.filter(
        (item) =>
          String(item?.manufacturer_part_number || "").trim() !== "" &&
          suggestedPartHasStock(item)
      )
    : [];
  const existingList = Array.isArray(line.substitutes)
    ? line.substitutes.filter(
        (item) =>
          String(item?.manufacturer_part_number || "").trim() !== "" &&
          suggestedPartHasStock(item)
      )
    : [];
  // Keep in-stock DigiKey view-options so "Change suggested part" still works.
  const preservedOptions =
    optionList.length > 0
      ? optionList
      : existingList.length > 0
      ? existingList
      : [alternate];

  const next = {
    ...line,
    ...digiKeyFields,
    unmatched_original_mpn: originalMpn,
    selected_alternate_mpn: mpn,
    selected_alternate_manufacturer: alternateManufacturer,
    selected_alternate_type: alternate.substitute_type || "",
    selected_alternate_group: alternate.substitute_group || "",
    alternate_selection_type: "unmatched",
    unmatched_warning: UNMATCHED_ALT_SELECTED_WARNING,
    is_unmatched: true,
    isUnmatched: true,
    substitutes: preservedOptions,
    substitutes_loaded: true,
    alternate_count: preservedOptions.length,
  };

  applyAlternateManufacturerToLine(line, next, alternateManufacturer);

  const previousSyncedMpn = String(line._missing_info_synced_mpn || "")
    .trim()
    .toUpperCase();
  if (previousSyncedMpn && previousSyncedMpn === mpn.toUpperCase()) {
    next._missing_info_synced = line._missing_info_synced === true;
    next._missing_info_synced_mpn = line._missing_info_synced_mpn;
  } else {
    next._missing_info_synced = false;
    next._missing_info_synced_mpn = "";
  }
  delete next.missing_info_synced;
  delete next.missing_info_synced_mpn;

  return preserveEnrichedDigiKeyColumns(
    line,
    preserveMountingClassification(line, next, partFields)
  );
};

/** Parse exported Excel MPN cells back into obsolete alternate metadata. */
const parseExportedAlternateMpnCell = (rawMpn, originalPrefix) => {
  const parts = String(rawMpn || "")
    .split(/\r?\n/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length < 2) {
    return null;
  }

  const prefixRe = new RegExp(`^${originalPrefix}:\\s*(.+)$`, "i");
  const originalLine = parts.find((part) => prefixRe.test(part));
  if (!originalLine) {
    return null;
  }

  const originalMpn = originalLine.replace(prefixRe, "$1").trim();
  const selectedMpn = parts[0];
  if (!selectedMpn || !originalMpn) {
    return null;
  }

  const recLine = parts.find(
    (part) => part !== selectedMpn && part !== originalLine,
  );
  const isAi = /^ai suggested$/i.test(String(recLine || ""));

  return {
    selectedMpn,
    originalMpn,
    recommendationText: recLine || "",
    isAi,
  };
};

export const applyObsoleteAlternateFromExcelLine = (line) => {
  if (!line || hasObsoleteAlternateSelected(line)) {
    return line;
  }

  const parsed = parseExportedAlternateMpnCell(
    line.manufacturer_part_number,
    "Obsolete",
  );
  if (!parsed) {
    return line;
  }

  return applySelectedAlternateToLine(
    {
      ...line,
      obsolete_part_number: parsed.originalMpn,
    },
    {
      manufacturer_part_number: parsed.selectedMpn,
      manufacturer:
        line.selected_alternate_manufacturer || line.manufacturer || "",
      substitute_group: parsed.isAi ? "ai" : "",
      substitute_type: parsed.isAi ? "AI Suggested" : "",
      recommendation_label: parsed.isAi ? "ai_suggested" : "",
      recommendation_text: parsed.isAi
        ? ALTERNATE_RECOMMENDATION_LABELS.ai_suggested
        : parsed.recommendationText,
    },
  );
};

/** Parse exported Excel MPN cells back into unmatched suggestion metadata. */
export const applyUnmatchedAlternateFromExcelLine = (line) => {
  if (!line || hasLineAlternateSelected(line) || isObsoleteLine(line)) {
    return line;
  }

  const parsed = parseExportedAlternateMpnCell(
    line.manufacturer_part_number,
    "Unmatched",
  );
  if (!parsed) {
    return line;
  }

  return applySelectedUnmatchedPartToLine(
    {
      ...line,
      unmatched_original_mpn: parsed.originalMpn,
    },
    {
      manufacturer_part_number: parsed.selectedMpn,
      manufacturer:
        line.selected_alternate_manufacturer || line.manufacturer || "",
      substitute_group: parsed.isAi ? "ai" : "view_options",
      substitute_type: parsed.isAi ? "AI Suggested" : "Suggested",
    },
  );
};

/** Merge obsolete enrichment metadata; use cached substitutes from digikey_enrichment when present. */
export const applyObsoleteEnrichmentToLine = (line, enrichmentPart) => {
  if (!line || !enrichmentPart?.isObsolete) {
    return line;
  }

  const cachedSubstitutes = mergeAiSuggestionsIntoSubstitutes(
    Array.isArray(enrichmentPart.substitutes) ? enrichmentPart.substitutes : [],
    enrichmentPart.ai_suggestions,
  );
  const previewAlternateCount = Array.isArray(enrichmentPart.alternateParts)
    ? enrichmentPart.alternateParts.length
    : 0;
  const substitutesLoaded =
    enrichmentPart.substitutesLoaded === true || cachedSubstitutes.length > 0;
  const substitutes = prependOriginalObsoleteAlternate(
    substitutesLoaded ? cachedSubstitutes : [],
    line,
    enrichmentPart,
  );
  const substitutesLoadedNext = substitutesLoaded || substitutes.length > 0;
  const alternateCount =
    substitutes.length > 0
      ? substitutes.length
      : enrichmentPart.alternateCount != null
      ? enrichmentPart.alternateCount
      : previewAlternateCount;
  const loadedWarning =
    substitutes.length > 0
      ? `Obsolete Part – ${substitutes.length} alternate(s) available. Click to select.`
      : OBSOLETE_NO_ALT_WARNING;
  const aiCacheFields = {
    ai_suggestions: Array.isArray(enrichmentPart.ai_suggestions)
      ? enrichmentPart.ai_suggestions
      : line.ai_suggestions || [],
    ai_suggestions_at:
      enrichmentPart.ai_suggestions_at || line.ai_suggestions_at || null,
    ai_suggestions_message:
      enrichmentPart.ai_suggestions_message || line.ai_suggestions_message || "",
    ai_suggestions_rejected: Array.isArray(enrichmentPart.ai_suggestions_rejected)
      ? enrichmentPart.ai_suggestions_rejected
      : line.ai_suggestions_rejected || [],
    ai_suggestions_attempted:
      enrichmentPart.ai_suggestions_attempted === true ||
      line.ai_suggestions_attempted === true ||
      (Array.isArray(enrichmentPart.ai_suggestions) &&
        enrichmentPart.ai_suggestions.length > 0),
  };
  const preselectAlternateMpn = String(
    enrichmentPart?.preselect_alternate_mpn || line.preselect_alternate_mpn || "",
  ).trim();
  const recommendationLabel = String(
    enrichmentPart?.recommendation_label || line.recommendation_label || "",
  ).trim();
  const recommendationText = String(
    enrichmentPart?.recommendation_text ||
      line.recommendation_text ||
      alternateRecommendationText(recommendationLabel),
  ).trim();

  const persistedSelectedMpn = String(
    line.selected_alternate_mpn || enrichmentPart?.selected_alternate_mpn || "",
  ).trim();

  if (persistedSelectedMpn) {
    const matchMpn = persistedSelectedMpn.toUpperCase();
    const substituteMatch =
      substitutes.find(
        (item) =>
          String(item.manufacturer_part_number || item.manufacturerPartNumber || "")
            .trim()
            .toUpperCase() === matchMpn,
      ) ||
      (Array.isArray(line.substitutes)
        ? line.substitutes.find(
            (item) =>
              String(item.manufacturer_part_number || item.manufacturerPartNumber || "")
                .trim()
                .toUpperCase() === matchMpn,
          )
        : null);
    const fallbackAlternate = {
      manufacturer_part_number: persistedSelectedMpn,
      manufacturer:
        line.selected_alternate_manufacturer ||
        enrichmentPart?.selected_alternate_manufacturer ||
        "",
    };
    const persistedAlternate = preferAlternateWithEnrichment(
      substituteMatch || fallbackAlternate,
      buildAlternateFromEnrichmentPart(enrichmentPart, persistedSelectedMpn) ||
        fallbackAlternate,
    );

    return applySelectedAlternateToLine(
      {
        ...line,
        is_obsolete: true,
        isObsolete: true,
        obsolete_part_number:
          line.obsolete_part_number || line.manufacturer_part_number,
        substitutes: substitutes.length ? substitutes : line.substitutes || [],
        substitutes_loaded: substitutesLoadedNext,
        quantity_available:
          enrichmentPart.quantity_available ?? line.quantity_available,
        unit_price:
          enrichmentPart.unit_price != null
            ? enrichmentPart.unit_price
            : line.unit_price,
        unit_price_display:
          enrichmentPart.unit_price_display || line.unit_price_display,
        ...aiCacheFields,
        alternate_count: alternateCount,
        preselect_alternate_mpn: preselectAlternateMpn,
        recommendation_label: recommendationLabel,
        recommendation_text: recommendationText,
      },
      persistedAlternate
    );
  }

  if (line.selected_alternate_mpn) {
    return {
      ...line,
      is_obsolete: true,
      isObsolete: true,
      obsolete_part_number: line.obsolete_part_number || line.manufacturer_part_number,
      substitutes: line.substitutes?.length
        ? prependOriginalObsoleteAlternate(
            line.substitutes,
            line,
            enrichmentPart,
          )
        : substitutes,
      substitutes_loaded: line.substitutes_loaded || substitutesLoadedNext,
      quantity_available:
        enrichmentPart.quantity_available ?? line.quantity_available,
      unit_price:
        enrichmentPart.unit_price != null
          ? enrichmentPart.unit_price
          : line.unit_price,
      unit_price_display:
        enrichmentPart.unit_price_display || line.unit_price_display,
      ...aiCacheFields,
      alternate_count: alternateCount,
      obsolete_warning: OBSOLETE_ALT_SELECTED_WARNING,
      preselect_alternate_mpn: preselectAlternateMpn,
      recommendation_label: recommendationLabel,
      recommendation_text: recommendationText,
    };
  }

  return {
    ...line,
    is_obsolete: true,
    isObsolete: true,
    obsolete_part_number: line.manufacturer_part_number,
    substitutes,
    substitutes_loaded: substitutesLoadedNext,
    quantity_available:
      enrichmentPart.quantity_available ?? line.quantity_available,
    unit_price:
      enrichmentPart.unit_price != null
        ? enrichmentPart.unit_price
        : line.unit_price,
    unit_price_display:
      enrichmentPart.unit_price_display || line.unit_price_display,
    ...aiCacheFields,
    alternate_count: alternateCount,
    preselect_alternate_mpn: preselectAlternateMpn,
    recommendation_label: recommendationLabel,
    recommendation_text: recommendationText,
    obsolete_warning:
      substitutesLoadedNext && substitutes.length > 0
        ? loadedWarning
        : enrichmentPart.obsoleteWarning || obsoleteAlternatesPrompt(alternateCount),
  };
};

/** Merge unmatched enrichment metadata from DigiKey My Lists Unmatched error. */
const enrichmentIndicatesUnmatched = (enrichmentPart) => {
  if (!enrichmentPart || enrichmentPart.isObsolete) {
    return false;
  }
  if (enrichmentPart.isUnmatched === true) {
    return true;
  }
  return hasUnmatchedDigiKeyError(enrichmentPart);
};

export const applyUnmatchedEnrichmentToLine = (line, enrichmentPart) => {
  if (!line || !enrichmentIndicatesUnmatched(enrichmentPart)) {
    return line;
  }

  const errors = Array.isArray(enrichmentPart.errors)
    ? enrichmentPart.errors
    : [];

  const persistedSelectedMpn = String(
    line.selected_alternate_mpn || enrichmentPart?.selected_alternate_mpn || ""
  ).trim();
  const persistedSelectionType = String(
    line.alternate_selection_type ||
      enrichmentPart?.alternate_selection_type ||
      ""
  ).trim();

  const baseLine = {
    ...line,
    is_unmatched: true,
    isUnmatched: true,
    unmatched_original_mpn: line.manufacturer_part_number,
    digikey_errors: errors,
    unmatched_warning: enrichmentPart.unmatchedWarning || UNMATCHED_DIGIKEY_WARNING,
    substitutes: Array.isArray(enrichmentPart?.substitutes)
      ? enrichmentPart.substitutes.filter(suggestedPartHasStock)
      : (line.substitutes || []).filter(suggestedPartHasStock),
    alternate_count: 0,
    substitutes_loaded: false,
  };
  baseLine.alternate_count = getUnmatchedViewOptionsCache(baseLine).length;
  baseLine.substitutes_loaded =
    baseLine.alternate_count > 0 || line.substitutes_loaded === true;

  if (
    persistedSelectedMpn &&
    (persistedSelectionType === "unmatched" || enrichmentPart?.isUnmatched)
  ) {
    const options = Array.isArray(enrichmentPart?.substitutes)
      ? enrichmentPart.substitutes
      : Array.isArray(line.substitutes)
      ? line.substitutes
      : [];
    const persistedAlternate =
      options.find(
        (item) =>
          String(item.manufacturer_part_number || "").trim() === persistedSelectedMpn
      ) ||
      buildAlternateFromEnrichmentPart(enrichmentPart, persistedSelectedMpn) ||
      {
        manufacturer_part_number: persistedSelectedMpn,
        manufacturer:
          line.selected_alternate_manufacturer ||
          enrichmentPart?.selected_alternate_manufacturer ||
          "",
      };

    return applySelectedUnmatchedPartToLine(baseLine, persistedAlternate);
  }

  return baseLine;
};

/** Build line_alternate_mpns array aligned to excelData / import order for quote API. */
export const buildLineAlternateMpns = (bomlines) => {
  const realLines = (bomlines || []).filter(isRealBomLine);
  const ordered = realLines.some((line) => line._bomLineIndex != null)
    ? [...realLines].sort(
        (a, b) => (a._bomLineIndex || 0) - (b._bomLineIndex || 0)
      )
    : realLines;

  return ordered.map((line) => {
    if (!lineUsesAlternateForQuote(line)) {
      return null;
    }
    return getQuoteMpn(line);
  });
};

/** Part-id keyed alternate selections for reliable quote API mapping. */
export const buildLineAlternateSelections = (bomlines) => {
  const realLines = (bomlines || []).filter(isRealBomLine);
  const ordered = realLines.some((line) => line._bomLineIndex != null)
    ? [...realLines].sort(
        (a, b) => (a._bomLineIndex || 0) - (b._bomLineIndex || 0)
      )
    : realLines;

  return ordered
    .map((line, index) => {
      if (!lineUsesAlternateForQuote(line)) {
        return null;
      }
      const alternateMpn = getQuoteMpn(line);
      if (!alternateMpn) {
        return null;
      }
      const isUnmatched = isUnmatchedLine(line);
      return {
        part_id: line.part_id || null,
        line_index: line._bomLineIndex != null ? line._bomLineIndex : index,
        selection_type: isUnmatched ? "unmatched" : "obsolete",
        obsolete_part_number: String(
          isUnmatched
            ? line.unmatched_original_mpn || line.manufacturer_part_number || ""
            : line.obsolete_part_number || line.manufacturer_part_number || ""
        ).trim(),
        alternate_mpn: alternateMpn,
        alternate_manufacturer: String(
          line.selected_alternate_manufacturer || ""
        ).trim(),
      };
    })
    .filter(Boolean);
};

/** Obsolete BOM lines that still need an alternate before quoting. */
export const getObsoleteLinesMissingAlternate = (bomlines) =>
  collectObsoleteBomLines(bomlines).filter(
    (line) => !hasLineAlternateSelected(line)
  );

/** Unmatched BOM lines that still need a suggested part before quoting. */
export const getUnmatchedLinesMissingSelection = (bomlines) =>
  collectUnmatchedBomLines(bomlines).filter(
    (line) => !hasLineAlternateSelected(line)
  );

/** Unmatched lines with no suggested part available — skip these during auto-quote. */
export const getUnmatchedLinesSkippedForQuote = (bomlines) =>
  collectUnmatchedBomLines(bomlines).filter(
    (line) =>
      !hasLineAlternateSelected(line) && getUnmatchedSuggestionCount(line) === 0
  );

/** Optional manufacturer names aligned with line_alternate_mpns for quote API. */
export const buildLineAlternateManufacturers = (bomlines) => {
  const realLines = (bomlines || []).filter(isRealBomLine);
  const ordered = realLines.some((line) => line._bomLineIndex != null)
    ? [...realLines].sort(
        (a, b) => (a._bomLineIndex || 0) - (b._bomLineIndex || 0)
      )
    : realLines;

  return ordered.map((line) => {
    const alt = line.selected_alternate_manufacturer;
    return alt != null && String(alt).trim() !== "" ? String(alt).trim() : null;
  });
};

/**
 * DigiKey My Lists tariffCode enum (from DigiKey UI):
 *   NoTariff | TariffPending | TariffApplied
 * DigiKey does not return a tariff % — only this status.
 * Display text matches DigiKey My Lists `displayTariffCode()`.
 */
export const formatUsImportTariff = (value) => {
  if (value == null || String(value).trim() === "") {
    return "N/A";
  }

  const raw = String(value).trim();
  const digiKeyLabels = {
    NoTariff: "No Tariff",
    TariffApplied: "May apply if shipping to the United States",
    TariffPending: "May apply if shipping to the United States",
  };

  if (digiKeyLabels[raw]) {
    return digiKeyLabels[raw];
  }

  // Legacy/unknown PascalCase codes
  if (/^[A-Za-z]+$/.test(raw) && /[a-z][A-Z]/.test(raw)) {
    return raw.replace(/([a-z])([A-Z])/g, "$1 $2");
  }

  return raw;
};

export const stripDigiKeyColumns = (bomlines) =>
  bomlines.map((line) => {
    const cleaned = { ...line };
    DIGIKEY_COLUMNS.forEach((col) => {
      delete cleaned[col];
    });
    delete cleaned.mounting_type;
    delete cleaned.category;
    delete cleaned._section;
    delete cleaned._section_title;
    return cleaned;
  });

export const buildDigiKeyFields = (part) => {
  if (!part) {
    return {
      "Digikey Description": "N/A",
      Datasheet: "N/A",
      Package: "N/A",
      "Lead Classification": "N/A",
      "Mounting Type": "N/A",
      mounting_type: "",
      category: "",
      "Part Status": {
        part_status: [
          { key: "ROHS Status", value: "N/A" },
          { key: "Lifecycle Status", value: "N/A" },
        ],
      },
      "U.S. Import Tariff": "N/A",
      "Country of Origin": "N/A",
    };
  }

  const mountingType = normalizeMountingType(
    part.mounting_type,
    categoryTextForMounting(part),
  );

  return {
    "Digikey Description": part.short_description || "N/A",
    Datasheet: part.datasheetLink
      ? {
          description: [
            {
              key: "",
              link: part.datasheetLink,
              linkText: "Datasheet",
            },
          ],
        }
      : "N/A",
    Package: resolvePackageValue(part) || "N/A",
    "Lead Classification":
      part.package_classification_label ||
      part.package_classification ||
      "Unknown",
    "Mounting Type": mountingType || "N/A",
    mounting_type: mountingType,
    category: categoryTextForMounting(part) || part.category || "",
    "Part Status": {
      part_status: [
        { key: "ROHS Status", value: resolveRohsStatus(part) || "N/A" },
        { key: "Lifecycle Status", value: part.lifecycleStatus || "N/A" },
      ],
    },
    "U.S. Import Tariff": formatUsImportTariff(part.usImportTariff),
    "Country of Origin":
      part.countryOfOrigin ||
      part.country_of_origin ||
      "N/A",
  };
};

/**
 * Classify a BOM line as SMT or THT using DigiKey mounting_type,
 * then Category, then description / package text.
 */
export const classifyMountingCategory = (line) => {
  if (!line || typeof line !== "object") {
    return "smt";
  }

  const normalized = normalizeMountingType(
    line.mounting_type || line["Mounting Type"],
    categoryTextForMounting(line),
  )
    .trim()
    .toLowerCase();
  const description = String(
    line["Digikey Description"] || line.description || "",
  )
    .trim()
    .toLowerCase();
  const pkg = String(line.Package || line.package || "")
    .trim()
    .toLowerCase();

  const throughHolePattern =
    /through[\s-]?hole|thru[\s-]?hole|(^|[^a-z])tht([^a-z]|$)/;
  const surfaceMountPattern =
    /surface[\s-]?mount|(^|[^a-z])smt([^a-z]|$)|(^|[^a-z])smd([^a-z]|$)/;

  if (normalized && normalized !== "n/a") {
    if (throughHolePattern.test(normalized)) {
      return "tht";
    }
    if (surfaceMountPattern.test(normalized)) {
      return "smt";
    }
  }

  const haystack = `${description} ${pkg}`;
  if (throughHolePattern.test(haystack)) {
    return "tht";
  }

  return "smt";
};

const getLinePackageText = (line) =>
  [
    line?.Package,
    line?.package,
    line?.package_case,
    line?.["Package / Case"],
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

/** SMT/TH for Assembly Specs: explicit mounting type, then Category. Never default N/A to SMT. */
const getAssemblyMountingCategory = (line) => {
  const mounting = normalizeMountingType(
    line?.mounting_type || line?.["Mounting Type"],
    categoryTextForMounting(line),
  )
    .trim()
    .toLowerCase();
  const throughHolePattern =
    /through[\s-]?hole|thru[\s-]?hole|(^|[^a-z])tht([^a-z]|$)/;
  const surfaceMountPattern =
    /surface[\s-]?mount|(^|[^a-z])smt([^a-z]|$)|(^|[^a-z])smd([^a-z]|$)/;

  if (throughHolePattern.test(mounting)) {
    return "tht";
  }
  if (surfaceMountPattern.test(mounting)) {
    return "smt";
  }
  return "unknown";
};

const getLineAssemblyQty = (line) => {
  const raw = line?.quantity ?? line?.Quantity ?? 0;
  const n = Number(String(raw).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/** Pin/solder joints only when package/description has an explicit pin count. */
const getThroughHoleSolderpoints = (line) => {
  const hay = `${getLinePackageText(line)} ${String(
    line?.["Digikey Description"] || line?.description || "",
  ).toLowerCase()}`;
  const pinMatch =
    hay.match(/(\d+)\s*(?:pin|pins|pos|position|positions|lead|leads)\b/) ||
    hay.match(/\b(?:dip|sip|pga)[-\s]?(\d+)\b/);
  const pins = pinMatch ? Number(pinMatch[1]) : 0;
  return Number.isFinite(pins) && pins > 0 ? pins : 0;
};

const classifyPackageFamily = (line) => {
  const text = getLinePackageText(line);
  if (/\bbga\b|ball[\s-]?grid/.test(text)) {
    return "BGA";
  }
  if (/\bqfn\b/.test(text)) {
    return "QFN";
  }
  if (/\bson\b/.test(text)) {
    return "SON";
  }
  return null;
};

/**
 * Assembly Specs for Excel export.
 * mode "lines" = one per BOM row; mode "qty" = Qty Per Assm totals.
 */
export const buildAssemblySpecs = (bomlines, mode = "lines") => {
  const byQty = mode === "qty";
  const lines = (bomlines || []).filter(isRealBomLine);
  let bomTotal = 0;
  let smt = 0;
  let th = 0;
  let thSolder = 0;
  let leadless = 0;
  let bga = 0;
  let son = 0;
  let qfn = 0;

  lines.forEach((line) => {
    const amount = byQty ? getLineAssemblyQty(line) : 1;
    bomTotal += amount;
    const mounting = getAssemblyMountingCategory(line);
    if (mounting === "smt") {
      smt += amount;
    } else if (mounting === "tht") {
      th += amount;
      const solderpoints = getThroughHoleSolderpoints(line);
      thSolder += byQty ? solderpoints * (getLineAssemblyQty(line) || 1) : solderpoints;
    }
    if (isLeadlessLine(line)) {
      leadless += amount;
    }
    const family = classifyPackageFamily(line);
    if (family === "BGA") {
      bga += amount;
    } else if (family === "SON") {
      son += amount;
    } else if (family === "QFN") {
      qfn += amount;
    }
  });

  return [
    ["BOM Lines", byQty ? bomTotal : lines.length],
    ["SMT", smt],
    ["TH", th],
    ["TH- Solderpoints", thSolder],
    ["LEAD LESS", leadless],
    ["BGA", bga],
    ["SON", son],
    ["QFN", qfn],
  ];
};

const buildSectionRow = (templateLine, section, title) => {
  const row = {};
  Object.keys(templateLine || {}).forEach((key) => {
    if (
      key === "_section" ||
      key === "_section_title" ||
      key === "_bomLineIndex" ||
      key === "designator_color" ||
      key === "mounting_type" ||
      key === "category"
    ) {
      return;
    }
    row[key] = "";
  });

  // Prefer a non-MPN column for the visible title so isRealBomLine stays false
  const titleKey =
    Object.keys(row).find(
      (key) =>
        key !== "designator_color" &&
        key !== "manufacturer_part_number" &&
        !isAttritionQtyColumn(key),
    ) || "description";

  if (titleKey) {
    row[titleKey] = title;
  }
  row.manufacturer_part_number = "";
  row._section = section;
  row._section_title = title;
  return row;
};

/**
 * Group SMT lines at the top and Through-Hole at the bottom,
 * with clear section separator rows. Preserves _bomLineIndex for quote mapping.
 * Total rows (if any) stay at the very end.
 */
export const organizeBomByMountingType = (bomlines) => {
  if (!bomlines || !bomlines.length) {
    return bomlines || [];
  }

  const withoutSections = bomlines.filter((line) => !isBomSectionRow(line));
  const totalRows = [];
  const workingLines = [];

  withoutSections.forEach((line, index) => {
    if (isBomTotalRow(line)) {
      totalRows.push(line);
      return;
    }
    // Drop empty spacer rows left over from ExcelJS sparse worksheets.
    if (!isRealBomLine(line) && isBlankBomLine(line)) {
      return;
    }
    workingLines.push({
      ...line,
      _bomLineIndex: line._bomLineIndex != null ? line._bomLineIndex : index,
    });
  });

  const smtLines = [];
  const thtLines = [];

  workingLines.forEach((line) => {
    if (isRealBomLine(line) && classifyMountingCategory(line) === "tht") {
      thtLines.push(line);
    } else if (isRealBomLine(line)) {
      smtLines.push(line);
    } else {
      // Keep non-blank helper rows (e.g. freight notes) with SMT block.
      smtLines.push(line);
    }
  });

  if (!thtLines.length) {
    return [...smtLines, ...totalRows];
  }

  const template =
    smtLines.find(isRealBomLine) ||
    thtLines.find(isRealBomLine) ||
    workingLines[0] ||
    {};

  const organized = [];
  if (smtLines.length) {
    organized.push(buildSectionRow(template, "smt", SMT_SECTION_TITLE));
    organized.push(...smtLines);
  }
  organized.push(buildSectionRow(template, "tht", THT_SECTION_TITLE));
  organized.push(...thtLines);
  organized.push(...totalRows);

  return organized;
};

const EXCEL_HEADER_ALIASES = {
  mfg_part: "manufacturer_part_number",
  mfg_part_number: "manufacturer_part_number",
  manufacturer_part_number: "manufacturer_part_number",
  mfgpartnumber: "manufacturer_part_number",
  mfg_part_no: "manufacturer_part_number",
  mfg_name: "manufacturer",
  mfgname: "manufacturer",
  manufacturer_name: "manufacturer",
  manufacturer: "manufacturer",
  qty_per_assm: "quantity",
  qtyperassembly: "quantity",
  qty: "quantity",
  quantity: "quantity",
  ref_designator: "designator",
  refdesignators: "designator",
  designator: "designator",
  description: "description",
  bomitemnumber: "item_number",
  bomitem: "item_number",
  partnumber: "customer_part_number",
  cust_part: "customer_part_number",
};

export const normalizeExcelHeaderKey = (header) => {
  const normalized = String(header || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  return EXCEL_HEADER_ALIASES[normalized] || header;
};

export const isExportMpnColumn = (columnKey) =>
  normalizeExcelHeaderKey(columnKey) === "manufacturer_part_number";

export const isExportManufacturerColumn = (columnKey) =>
  normalizeExcelHeaderKey(columnKey) === "manufacturer";

const findLineKeysByNormalizedHeader = (line, canonical) =>
  Object.keys(line || {}).filter(
    (key) => normalizeExcelHeaderKey(key) === canonical
  );

const applyNormalizedMountingType = (line) => {
  if (!line || isBomSectionRow(line) || isBomTotalRow(line)) {
    return line;
  }
  const normalized = normalizeMountingType(
    line.mounting_type || line["Mounting Type"],
    categoryTextForMounting(line),
  );
  if (!normalized) {
    return line;
  }
  const currentLabel = String(line["Mounting Type"] || "").trim();
  if (
    currentLabel.toLowerCase() === normalized.toLowerCase() &&
    String(line.mounting_type || "").trim() === normalized
  ) {
    return line;
  }
  return {
    ...line,
    mounting_type: normalized,
    "Mounting Type": normalized,
  };
};

/** Apply selected alternate MPN/manufacturer onto lines used for Excel export. */
export const prepareBomlinesForExport = (bomlines, excelData = []) => {
  const lines = bomlines || [];
  const alternateByIndex = new Map();

  [...lines, ...(excelData || [])].forEach((line) => {
    if (line?._bomLineIndex != null && hasLineAlternateSelected(line)) {
      alternateByIndex.set(line._bomLineIndex, line);
    }
  });

  return lines.map((line) => {
    if (isBomSectionRow(line) || isBomTotalRow(line)) {
      return line;
    }

    const source =
      (hasLineAlternateSelected(line) && line) ||
      (line?._bomLineIndex != null
        ? alternateByIndex.get(line._bomLineIndex)
        : null);

    if (!source || !hasLineAlternateSelected(source)) {
      return applyNormalizedMountingType(line);
    }

    const next = { ...line };
    const selectedMpn = String(source.selected_alternate_mpn).trim();
    const selectedMfg = String(source.selected_alternate_manufacturer || "").trim();
    const originalMpn = String(
      source.obsolete_part_number ||
        source.unmatched_original_mpn ||
        line.obsolete_part_number ||
        line.unmatched_original_mpn ||
        line.manufacturer_part_number ||
        ""
    ).trim();

    const mpnKeys = findLineKeysByNormalizedHeader(line, "manufacturer_part_number");
    if (mpnKeys.length) {
      mpnKeys.forEach((key) => {
        next[key] = selectedMpn;
      });
    } else {
      next.manufacturer_part_number = selectedMpn;
    }

    if (selectedMfg) {
      const mfgKeys = findLineKeysByNormalizedHeader(line, "manufacturer");
      if (mfgKeys.length) {
        mfgKeys.forEach((key) => {
          next[key] = selectedMfg;
        });
      } else {
        next.manufacturer = selectedMfg;
      }
    }

    next.selected_alternate_mpn = selectedMpn;
    if (selectedMfg) {
      next.selected_alternate_manufacturer = selectedMfg;
    }
    if (isUnmatchedLine(source) || isUnmatchedLine(line)) {
      if (originalMpn) {
        next.unmatched_original_mpn = originalMpn;
      }
      next.is_unmatched = true;
      next.isUnmatched = true;
    } else if (originalMpn) {
      next.obsolete_part_number = originalMpn;
      next.is_obsolete = true;
      next.isObsolete = true;
    }

    return applyNormalizedMountingType(next);
  });
};

export const enrichBomlinesWithDigiKey = (bomlines, digikeyEnrichment) => {
  const cleaned = stripDigiKeyColumns(bomlines);
  const enrichmentByPart = {};
  const enrichmentByAlternate = {};

  (digikeyEnrichment.parts || []).forEach((part) => {
    if (part.part_number) {
      enrichmentByPart[String(part.part_number).trim().toUpperCase()] = part;
    }
    const alternateMpn = String(part.selected_alternate_mpn || "").trim().toUpperCase();
    if (alternateMpn) {
      enrichmentByAlternate[alternateMpn] = part;
    }
  });

  return cleaned.map((line, index) => {
    const bomLineIndex =
      line._bomLineIndex != null ? line._bomLineIndex : index;

    // Section / total / blank Excel rows must not get DigiKey "N/A" filler cells.
    if (
      isBomSectionRow(line) ||
      isBomTotalRow(line) ||
      !isRealBomLine(line)
    ) {
      return {
        ...line,
        _bomLineIndex: bomLineIndex,
      };
    }

    const lineMpn = resolveManufacturerPartNumber(line);
    const enrichmentPart =
      enrichmentByPart[lineMpn] || enrichmentByAlternate[lineMpn];
    const enriched = {
      ...line,
      ...buildDigiKeyFields(enrichmentPart),
      _bomLineIndex: bomLineIndex,
    };

    if (enrichmentPart?.isObsolete) {
      return applyObsoleteEnrichmentToLine(enriched, enrichmentPart);
    }

    return applyUnmatchedEnrichmentToLine(enriched, enrichmentPart);
  });
};

export const applyImportDigiKeyColumns = (bomlines, digikeyEnrichment) =>
  organizeBomByMountingType(
    enrichBomlinesWithDigiKey(bomlines, digikeyEnrichment),
  );

const resolveManufacturerPartNumber = (line) => {
  if (
    line.manufacturer_part_number != null &&
    String(line.manufacturer_part_number).trim() !== ""
  ) {
    return String(line.manufacturer_part_number).trim().toUpperCase();
  }

  for (const [key, value] of Object.entries(line)) {
    if (
      normalizeExcelHeaderKey(key) === "manufacturer_part_number" &&
      value != null &&
      String(value).trim() !== ""
    ) {
      return String(value).trim().toUpperCase();
    }
  }

  return "";
};

// export const getTableColumnKeys = (bomlines) => {
//   if (!bomlines || !bomlines.length) {
//     return [];
//   }

//   const keySource =
//     bomlines.find((line) => isRealBomLine(line) && !isBomSectionRow(line)) ||
//     bomlines.find((line) => !isBomSectionRow(line)) ||
//     bomlines[0];

//   const keys = Object.keys(keySource).filter(
//     (key) =>
//       key !== "designator_color" &&
//       key !== "mounting_type" &&
//       key !== "_section" &&
//       key !== "_section_title" &&
//       key !== "_bomLineIndex" &&
//       key !== "part_id",
//   );

//   DIGIKEY_COLUMNS.forEach((col) => {
//     if (!keys.includes(col)) {
//       keys.push(col);
//     }
//   });

//   const attritionCols = keys.filter(isAttritionQtyColumn);
//   if (!attritionCols.length) {
//     return keys;
//   }

//   const withoutAttrition = keys.filter((key) => !isAttritionQtyColumn(key));
//   const quantityIndex = withoutAttrition.indexOf("quantity");

//   if (quantityIndex === -1) {
//     return [...withoutAttrition, ...attritionCols];
//   }

//   var result = [
//     ...withoutAttrition.slice(0, quantityIndex + 1),
//     ...attritionCols,
//     ...withoutAttrition.slice(quantityIndex + 1),
//   ];
//   console.log("getTableColumnKeys=>", result);
//   return result;
// };

export const getTableColumnKeys = (bomlines) => {
  if (!bomlines || !bomlines.length) {
    return [];
  }

  const mergedKeys = {};
  (bomlines || []).forEach((line) => {
    if (!line || typeof line !== "object") {
      return;
    }
    if (isBomSectionRow(line) || isBomTotalRow(line)) {
      return;
    }
    Object.keys(line).forEach((key) => {
      if (!(key in mergedKeys)) {
        mergedKeys[key] = line[key];
      }
    });
  });

  const keySource =
    Object.keys(mergedKeys).length > 0
      ? mergedKeys
      : bomlines.find((line) => isRealBomLine(line) && !isBomSectionRow(line)) ||
        bomlines.find((line) => !isBomSectionRow(line)) ||
        bomlines[0];

  let keys = Object.keys(keySource).filter(
    (key) =>
      !String(key).startsWith("_") &&
      key !== "designator_color" &&
      key !== "mounting_type" &&
      key !== "category" &&
      key !== "part_id" &&
      key !== "is_obsolete" &&
      key !== "isObsolete" &&
      key !== "is_unmatched" &&
      key !== "isUnmatched" &&
      key !== "obsolete_part_number" &&
      key !== "unmatched_original_mpn" &&
      key !== "selected_alternate_mpn" &&
      key !== "selected_alternate_manufacturer" &&
      key !== "selected_alternate_type" &&
      key !== "selected_alternate_group" &&
      key !== "selected_alternate_unit_price" &&
      key !== "digikey_part_number" &&
      key !== "quantity_available" &&
      key !== "unit_price" &&
      key !== "unit_price_display" &&
      key !== "ai_suggestions" &&
      key !== "ai_suggestions_at" &&
      key !== "ai_suggestions_message" &&
      key !== "ai_suggestions_rejected" &&
      key !== "ai_suggestions_attempted" &&
      key !== "alternate_selection_type" &&
      key !== "obsolete_warning" &&
      key !== "unmatched_warning" &&
      key !== "digikey_errors" &&
      key !== "substitutes" &&
      key !== "substitutes_loaded" &&
      key !== "alternate_count" &&
      key !== "preselect_alternate_mpn" &&
      key !== "recommendation_label" &&
      key !== "recommendation_text" &&
      key !== "missing_info_synced" &&
      key !== "missing_info_synced_mpn"
  );

  DIGIKEY_COLUMNS.forEach((col) => {
    if (!keys.includes(col)) {
      keys.push(col);
    }
  });

  // -----------------------------
  // Attrition columns
  // -----------------------------
  const attritionCols = keys.filter(isAttritionQtyColumn);

  let withoutAttrition = keys.filter((key) => !isAttritionQtyColumn(key));

  const quantityIndex = withoutAttrition.indexOf("quantity");

  if (quantityIndex === -1) {
    keys = [...withoutAttrition, ...attritionCols];
  } else {
    keys = [
      ...withoutAttrition.slice(0, quantityIndex + 1),
      ...attritionCols,
      ...withoutAttrition.slice(quantityIndex + 1),
    ];
  }

  // -----------------------------
  // Quote column groups
  // -----------------------------

  const quoteIndexes = new Set();

  keys.forEach((key) => {
    let match;

    match = key.match(/^QuoteQty (\d+)$/);
    if (match) {
      quoteIndexes.add(Number(match[1]));
      return;
    }

    match = key.match(/^BOM Manager Result (\d+)$/);
    if (match) {
      quoteIndexes.add(Number(match[1]));
      return;
    }

    match = key.match(/^Quote (\d+)$/);
    if (match) {
      quoteIndexes.add(Number(match[1]));
      return;
    }

    match = key.match(/^Tariff Charge (\d+)$/);
    if (match) {
      quoteIndexes.add(Number(match[1]));
    }
  });

  const sortedQuoteIndexes = [...quoteIndexes].sort((a, b) => a - b);

  // Remove all quote-related columns from their existing positions
  keys = keys.filter(
    (key) =>
      !/^QuoteQty \d+$/.test(key) &&
      !/^BOM Manager Result \d+$/.test(key) &&
      !/^Quote \d+$/.test(key) &&
      !/^Tariff Charge \d+$/.test(key),
  );

  // Build each quote group
  const quoteGroups = [];

  sortedQuoteIndexes.forEach((index) => {
    const hasQuoteColumn =
      Object.prototype.hasOwnProperty.call(keySource, `Quote ${index}`) ||
      Object.prototype.hasOwnProperty.call(keySource, `QuoteQty ${index}`) ||
      Object.prototype.hasOwnProperty.call(
        keySource,
        `BOM Manager Result ${index}`,
      );
    if (!hasQuoteColumn) {
      return;
    }
    const group = [
      `QuoteQty ${index}`,
      `BOM Manager Result ${index}`,
      `Quote ${index}`,
      `Tariff Charge ${index}`,
    ].filter((column) => {
      if (column === `BOM Manager Result ${index}`) {
        return (
          Object.prototype.hasOwnProperty.call(keySource, column) ||
          Object.prototype.hasOwnProperty.call(keySource, `Quote ${index}`)
        );
      }
      return Object.prototype.hasOwnProperty.call(keySource, column);
    });

    quoteGroups.push(...group);
  });

  // Add all quote groups together
  keys.push(...quoteGroups);
  return keys;
};
export const getExcelCellText = (cell) => {
  if (!cell || cell._value == null) {
    return "";
  }

  const model = cell._value.model;
  if (!model) {
    return "";
  }

  const val = model.value;
  if (val == null) {
    return "";
  }

  if (typeof val === "object" && val.richText) {
    return val.richText.map((part) => part.text || "").join("");
  }

  if (typeof val === "object" && val.formula != null) {
    return val.result != null ? String(val.result) : "";
  }

  return String(val);
};

export const parseExcelRowsToBomlines = (excelRows) => {
  if (!excelRows || excelRows.length < 2) {
    return [];
  }

  const headerRow = excelRows[0];
  const columnNumbers = Object.keys(headerRow._cells || {})
    .map(Number)
    .filter((num) => !Number.isNaN(num))
    .sort((a, b) => a - b);
  const headers = columnNumbers.map((colNum) =>
    getExcelCellText(headerRow._cells[colNum]),
  );
  const bomlines = [];

  for (let rowIndex = 1; rowIndex < excelRows.length; rowIndex++) {
    const row = excelRows[rowIndex];
    if (!row || !row._cells) {
      continue;
    }

    const line = {};

    columnNumbers.forEach((colNum, headerIndex) => {
      const header = headers[headerIndex];
      if (!header) {
        return;
      }

      const codeKey = normalizeExcelHeaderKey(header);
      line[codeKey] = getExcelCellText(row._cells[colNum]);
    });

    if (isBlankBomLine(line)) {
      continue;
    }

    bomlines.push(line);
  }

  return bomlines;
};

/** Yellow footer rows matching Home Freight / Tariff Charge summary. */
export const FREIGHT_TARIFF_FOOTER_ARGB = "eec950";

const SKIP_QUOTED_VENDOR_STATUSES =
  /^(no result|not available|multiple manufacturers|not applicable|n\/a)$/i;
const RESULT_STATUS_PREFIX =
  /^(insufficient\s+quan[ti]ity)\s+/i;

const getResultCellPlainText = (cell) => {
  if (cell == null || cell === "") {
    return "";
  }
  if (typeof cell === "string" || typeof cell === "number") {
    return String(cell);
  }
  if (typeof cell === "object") {
    const title = String(cell.title || "").trim();
    let body = "";
    if (Array.isArray(cell.text)) {
      body = cell.text.map((part) => part?.text || "").join("");
    } else if (Array.isArray(cell.richText)) {
      body = cell.richText.map((part) => part?.text || "").join("");
    }
    return [title, body].filter(Boolean).join(" ");
  }
  return "";
};

const normalizeQuotedVendorName = (vendor) => {
  let name = String(vendor || "").trim();
  if (/^ORIGINAL_/i.test(name)) {
    name = name.replace(/^ORIGINAL_/i, "").trim();
  }
  return name;
};

/** Winning distributor name from a BOM Manager Result cell, or empty. */
export const getQuotedVendorNameFromResultCell = (cell) => {
  const normalized = getResultCellPlainText(cell)
    .replace(/\s+/g, " ")
    .replace(RESULT_STATUS_PREFIX, "")
    .trim();
  if (!normalized) {
    return "";
  }

  const parts = normalized
    .split(" | ")
    .map((part) => part.trim())
    .filter(Boolean);
  if (!parts.length || SKIP_QUOTED_VENDOR_STATUSES.test(parts[0])) {
    return "";
  }

  const costIndex = parts.findIndex((part) => /^cost\s*:/i.test(part));
  const vendor =
    costIndex >= 1 ? parts[costIndex - 1] : parts.length >= 3 ? parts[2] : "";
  if (
    !vendor ||
    /^cost\s*:/i.test(vendor) ||
    /^available\s*:/i.test(vendor) ||
    /^price break/i.test(vendor) ||
    /^moq\b/i.test(vendor)
  ) {
    return "";
  }
  return normalizeQuotedVendorName(vendor);
};

const resultKeysForLine = (line, quoteSlot = null) => {
  if (quoteSlot != null) {
    return [`BOM Manager Result ${quoteSlot}`];
  }
  const fromLine = Object.keys(line || {}).filter((key) =>
    /^BOM Manager Result \d+$/.test(key),
  );
  if (fromLine.length) {
    return fromLine;
  }
  return Array.from({ length: 10 }, (_, index) => `BOM Manager Result ${index + 1}`);
};

/**
 * Unique distributors that actually won quoted lines (what the Result column shows).
 * Pass quoteSlot to count vendors for one Quote N column only.
 */
export const collectUniqueQuotedVendors = (bomlines, quoteSlot = null) => {
  const vendors = new Set();

  (bomlines || []).forEach((line) => {
    if (!line || isBomSectionRow(line) || isBomTotalRow(line)) {
      return;
    }
    resultKeysForLine(line, quoteSlot).forEach((key) => {
      if (line[key] == null || line[key] === "") {
        return;
      }
      const vendor = getQuotedVendorNameFromResultCell(line[key]);
      if (vendor) {
        vendors.add(vendor);
      }
    });
  });

  return [...vendors];
};

export const countUniqueQuotedVendors = (bomlines, quoteSlot = null) =>
  collectUniqueQuotedVendors(bomlines, quoteSlot).length;

export const getFreightTotalFromDetails = (freightDetails) => {
  let details = freightDetails;
  if (typeof details === "string") {
    try {
      details = JSON.parse(details);
    } catch (e) {
      return 0;
    }
  }
  if (!details || typeof details !== "object") {
    return 0;
  }
  const total = Number(details.total);
  return Number.isFinite(total) ? total : 0;
};

/** Sum tariff from stored tariff_details (supports tariff_totals or nested values). */
export const getTariffTotalFromDetails = (tariffDetails) => {
  let details = tariffDetails;
  if (typeof details === "string") {
    try {
      details = JSON.parse(details);
    } catch (e) {
      return 0;
    }
  }
  if (!details || typeof details !== "object") {
    return 0;
  }

  const storedTotal = Number(details.tariff_totals?.total);
  if (Number.isFinite(storedTotal)) {
    return storedTotal;
  }

  let sum = 0;
  Object.keys(details).forEach((key) => {
    if (key === "tariff_percentage" || key === "tariff_totals") {
      return;
    }
    const byQuote = details[key];
    if (!byQuote || typeof byQuote !== "object") {
      return;
    }
    Object.values(byQuote).forEach((vendors) => {
      if (!vendors || typeof vendors !== "object") {
        return;
      }
      Object.values(vendors).forEach((entry) => {
        const value = Number(entry?.tariffValue);
        if (Number.isFinite(value)) {
          sum += value;
        }
      });
    });
  });

  return Math.round(sum * 100) / 100;
};

export const formatMoneyAmount = (amount, currency = "$") => {
  const value = Number(amount);
  const safe = Number.isFinite(value) ? value : 0;
  return `${currency}${safe.toFixed(2)}`;
};
